import { makeAutoObservable } from 'mobx';

import { apiFetch } from 'framework/api';
import { newGuid } from 'tools/guid';
import { MobxManager } from 'framework/mobx-integration';
import { RemoteEventsManager } from 'core/remoteEventsManager';
import { RemoteEventsSubscription } from 'core/remoteEventsManagerTypes';
import { DataSourceEvent } from 'framework/entities/events';
import { DataSourceManager } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/dataSourceManager';
import { clearDatasourceCache, DataSourceDataItem } from 'controls/designer/features/widgets/datasources/api';

import { DatasourceValueEntry } from './types';

export class DatasourceValueStore {
	row: DataSourceDataItem | null = null;

	private manager: DataSourceManager;
	private requestId = newGuid();
	private subscription: RemoteEventsSubscription | null = null;
	private mobx = new MobxManager();

	constructor(private entry: DatasourceValueEntry) {
		makeAutoObservable(this);

		this.manager = new DataSourceManager({
			selection: entry.dataSourceSelection,
			loadDatasourceList: false,
			loadData: true,
			requestId: this.requestId,
		});

		this.mobx.reaction(
			() => this.manager.data.data,
			(data) => {
				const { rowKey, rowKeyValue } = entry.dataSourceSelection;
				this.row = data.find((r) => r[rowKey ?? ''] == rowKeyValue) ?? null;
				this.subscribe();
			},
			{ fireImmediately: true },
		);
	}

	private subscribe() {
		this.subscription?.unsubscribe();
		const { dataSourceId, accountId } = this.entry.dataSourceSelection;
		if (!dataSourceId) return;
		this.subscription = RemoteEventsManager.subscribeCallback(
			[DataSourceEvent.subscription(dataSourceId, accountId)],
			() => this.manager.data.reload(),
		);
	}

	get value(): string {
		const { placeholder, displayColumn } = this.entry;
		if (!this.row || !displayColumn) return placeholder ?? '';
		return this.row[displayColumn]?.toString() ?? placeholder ?? '';
	}

	destroy() {
		this.mobx.destroy();
		this.subscription?.unsubscribe();
		this.manager.destroy();
		void apiFetch(
			clearDatasourceCache({ accountId: this.entry.dataSourceSelection.accountId, requestId: this.requestId }),
		);
	}
}
